import * as React from 'react'
import type { ElementRef } from 'react'
import { clsx } from 'clsx'

import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  MastodonIcon,
  PinterestIcon,
  StartPageIcon,
  SubstackIcon,
  BlueskyIcon,
  ThreadsIcon,
  TiktokIcon,
  TwitterIcon,
  WarningIcon,
  WhatsappIcon,
  YoutubeIcon,
  GbpIcon,
} from '../icons'
import type { IconProps } from '../icons'

import styles from './ChannelIcon.module.css'

type ChannelType =
  | 'facebook'
  | 'googlebusiness'
  | 'instagram'
  | 'instagram_login_api'
  | 'linkedin'
  | 'mastodon'
  | 'pinterest'
  | 'startPage'
  | 'tiktok'
  | 'twitter'
  | 'youtube'
  | 'x'
  | 'threads'
  | 'bluesky'
  | 'substack'
  | 'whatsapp'

const channelIconByType = {
  facebook: FacebookIcon,
  // todo: rename!!
  googlebusiness: GbpIcon,
  instagram: InstagramIcon,
  instagram_login_api: InstagramIcon,
  linkedin: LinkedinIcon,
  mastodon: MastodonIcon,
  pinterest: PinterestIcon,
  startPage: StartPageIcon,
  tiktok: TiktokIcon,
  twitter: TwitterIcon,
  x: TwitterIcon,
  youtube: YoutubeIcon,
  threads: ThreadsIcon,
  bluesky: BlueskyIcon,
  substack: SubstackIcon,
  whatsapp: WhatsappIcon,
}

type ChannelColors = {
  [key in ChannelType]: string
}

const channelColors: ChannelColors = {
  facebook: 'var(--global-color-channels-facebook)',
  googlebusiness: 'var(--global-color-channels-google-business-profile)',
  instagram: 'var(--global-color-channels-instagram)',
  instagram_login_api: 'var(--global-color-channels-instagram)',
  linkedin: 'var(--global-color-channels-linkedin)',
  bluesky: 'var(--global-color-channels-bluesky)',
  mastodon: 'var(--global-color-channels-mastodon)',
  pinterest: 'var(--global-color-channels-pinterest)',
  startPage: 'var(--global-color-channels-start-page)',
  tiktok: 'var(--global-color-channels-tiktok)',
  x: 'var(--global-color-channels-x)',
  twitter: 'var(--global-color-channels-x)',
  youtube: 'var(--global-color-channels-youtube)',
  threads: 'var(--global-color-channels-threads)',
  substack: 'var(--global-color-channels-substack)',
  whatsapp: 'var(--global-color-channels-whatsapp)',
}

type ChannelIconProps = Omit<IconProps, 'color'> & {
  /**
   * Type of the channel
   */
  type: ChannelType
  /**
   * Color of the icon, use `branded` for the branded color of the channel
   */
  color?: 'default' | 'subtle' | 'inverted' | 'branded'
  /**
   * Classname of the ChannelIcon
   */
  className?: string
}

interface ChannelIconCssProperties extends React.CSSProperties {
  '--channel-icon-color': string
}

const ChannelIcon = React.forwardRef<ElementRef<'svg'>, ChannelIconProps>(
  (
    { type, color = 'default', className, ...props }: ChannelIconProps,
    forwardedRef,
  ) => {
    // TODO: when we have question mark icon, warning icon should be replaced with it
    const Icon = channelIconByType[type] ?? WarningIcon

    const iconStyle: ChannelIconCssProperties = {
      '--channel-icon-color': channelColors[type],
    }

    return (
      <Icon
        style={iconStyle}
        ref={forwardedRef}
        {...props}
        data-channel={type}
        color={color !== 'branded' ? color : undefined}
        className={clsx(
          color === 'branded' && styles.withColor,
          color === 'default' && styles.iconColor,
          className,
        )}
      />
    )
  },
)

ChannelIcon.displayName = 'ChannelIcon'

export { ChannelIcon, channelColors }
export type { ChannelIconProps }
export type { ChannelType }
